import type { Lang } from "../lib/i18n";
import { pick } from "../lib/i18n";
import type { UserProfile, Person } from "../lib/auth";
import { ORG_CHART_URL } from "../lib/contacts";

function OrgAvatar({ person, size }: { person: { name: string; photo: string | null }; size?: "sm" }) {
  const cls = "kd-org-avatar" + (size === "sm" ? " kd-org-avatar-sm" : "");
  return person.photo ? (
    <div className={cls}>
      <img src={person.photo} alt={person.name} />
    </div>
  ) : (
    <div className={cls}>{(person.name || "?")[0]}</div>
  );
}

/** Organization chart: manager chain (up to 3), the user, and up to 6 peers. */
export function OrgChart({ lang, profile }: { lang: Lang; profile: UserProfile }) {
  const t = (ar: string, en: string) => pick(lang, ar, en);
  const chain = (profile.managerChain || []).slice(0, 3);
  const directManager = profile.managerPerson;
  const peers: Person[] = (profile.peerPersons || []).slice(0, 6);

  if (!(chain.length > 0 || peers.length > 0)) return null;

  // Senior leadership above the direct manager, top-down.
  const seniors = chain.slice(1).reverse();

  return (
    <div className="kd-org-chart">
      <h3 className="kd-org-title">{t("الهيكل التنظيمي", "Organization Chart")}</h3>
      <p className="kd-org-subtitle">{t("تعرّف على تسلسل الإدارة وزملائك في الفريق.", "Get familiar with your management chain and team peers.")}</p>

      <div className="kd-org-tree">
        {seniors.map((name, i) => (
          <div className="kd-org-level" key={`mgr-up-${i}`}>
            <div className="kd-org-node kd-org-node-manager">
              <OrgAvatar person={{ name, photo: null }} />
              <div className="kd-org-info">
                <strong>{name}</strong>
                <small>{t("إدارة عليا", "Senior Leadership")}</small>
              </div>
            </div>
            <div className="kd-org-line" />
          </div>
        ))}

        {directManager && (
          <div className="kd-org-level">
            <div className="kd-org-node kd-org-node-manager">
              <OrgAvatar person={directManager} />
              <div className="kd-org-info">
                <strong>{directManager.name}</strong>
                <small>{directManager.title || t("المدير المباشر", "Direct Manager")}</small>
              </div>
            </div>
            <div className="kd-org-line" />
          </div>
        )}

        <div className="kd-org-level">
          <div className="kd-org-node kd-org-node-self">
            <div className="kd-org-avatar kd-org-avatar-self">
              {profile.photo ? <img src={profile.photo} alt={profile.name} /> : (profile.name || "?")[0]}
            </div>
            <div className="kd-org-info">
              <strong>{profile.name || t("أنت", "You")}</strong>
              <small>
                {t("أنت", "You")}
                {profile.role ? `${t("،", ",")} ${profile.role}` : ""}
              </small>
            </div>
          </div>
        </div>

        {peers.length > 0 && (
          <>
            <div className="kd-org-line kd-org-line-down" />
            <div className="kd-org-peers-label">
              {t("زملاؤك في الفريق", "Your team peers")}
              <span className="kd-org-peers-count">{peers.length}</span>
            </div>
            <div className="kd-org-peers">
              {peers.map((p, i) => (
                <div className="kd-org-node kd-org-node-peer" key={i}>
                  <OrgAvatar person={p} size="sm" />
                  <div className="kd-org-info">
                    <strong className="kd-org-peer-name">{p.name}</strong>
                    {p.title && <small className="kd-org-peer-title">{p.title}</small>}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      <p className="kd-org-hint">
        {t("يمكنك استعراض الهيكل الوظيفي الكامل للشركة لاحقاً من ", "You can explore the full company org chart later from ")}
        <a href={ORG_CHART_URL} target="_blank" rel="noopener noreferrer">
          {t("هنا", "here")}
        </a>
        .
      </p>
    </div>
  );
}
